import { getBootstrapData } from '@experience/core-hostapp-share-lib';
import {
  useAnalytics as useAnalyticsBase,
  type TAnalyticsEventsMap,
  type TConvertEventsMap,
  type TTrackEvent,
} from '@experience/frontend-analytics';
import { makeUseViewTracker, makeUseClickTracker } from '@experience/vue-analytics-directives';
import { ref } from 'vue';

import type { EventsMap } from '@/shared/api/generated/sogu/types.gen';
import { MF_NAME } from '@/shared/config/env';
import { getRuntimeConfig } from '@/shared/config/runtime';
import { makeSingleton } from '@/shared/utils/makeSingleton';

/** ID достопримечательности, страницу которой смотрим. Общий параметр всех событий. */
export const sightId = ref<number | null>(null);

export const setSightId = (id: number) => {
  sightId.value = id;
};

type StripSightId<P> = Omit<P, 'sight_id'>;

/** Схема требует sight_id у каждого события, в вызовах его подставляет обёртка. */
type PublicEventsMap<T extends TAnalyticsEventsMap> = {
  [K in keyof T]: T[K] extends never ? never : keyof StripSightId<T[K]> extends never ? never : StripSightId<T[K]>;
};

const withCommonParams =
  <T extends TAnalyticsEventsMap>(track: TTrackEvent<T>): TTrackEvent<PublicEventsMap<T>> =>
  (eventName, ...args) => {
    const [params] = args;
    const eventParams = {
      ...(params != null && typeof params === 'object' ? params : {}),
      sight_id: sightId.value,
    };

    const send = track as (name: typeof eventName, params: Record<string, unknown>) => Promise<void>;

    return send(eventName, eventParams);
  };

const createAnalytics = () => {
  const { userId, deviceId } = getBootstrapData();
  const { VITE_RUNTIME_SOGU_SERVICE_URL } = getRuntimeConfig();

  type FullEventsMap = TConvertEventsMap<EventsMap>;
  type AnalyticsEventsMap = PublicEventsMap<FullEventsMap>;

  const { trackEvent: trackEventBase, trackEventImmediately: trackEventImmediatelyBase } =
    useAnalyticsBase<FullEventsMap>({
      soguURL: VITE_RUNTIME_SOGU_SERVICE_URL,
      appName: MF_NAME,
      appVersion: __APP_VERSION__,
      userId: userId ? String(userId) : null,
      deviceId,
    });

  const trackEvent = withCommonParams(trackEventBase);
  const trackEventImmediately = withCommonParams(trackEventImmediatelyBase);

  /**
   * Позволяет отслеживать просмотры элементов на странице.
   *
   * @param eventName analytics имя события
   * @param eventParams analytics параметры события
   * @returns vue директива
   */
  const useViewTracker = makeUseViewTracker<AnalyticsEventsMap>(trackEvent);

  /**
   * Позволяет отслеживать просмотры элементов на странице.
   *
   * Отправляет событие даже после ухода со страницы.
   *
   * @param eventName analytics имя события
   * @param eventParams analytics параметры события
   * @returns vue директива
   */
  const useImmediateViewTracker = makeUseViewTracker<AnalyticsEventsMap>(trackEventImmediately);

  /**
   * Позволяет отслеживать клики на элементах на странице.
   *
   * @param eventName analytics имя события
   * @param eventParams analytics параметры события
   * @returns vue директива
   */
  const useClickTracker = makeUseClickTracker<AnalyticsEventsMap>(trackEvent);

  /**
   * Позволяет отслеживать клики на элементах на странице.
   *
   * Отправляет событие даже после ухода со страницы.
   *
   * @param eventName analytics имя события
   * @param eventParams analytics параметры события
   * @returns vue директива
   */
  const useImmediateClickTracker = makeUseClickTracker<AnalyticsEventsMap>(trackEventImmediately);

  return {
    trackEvent,
    trackEventImmediately,
    useViewTracker,
    useImmediateViewTracker,
    useClickTracker,
    useImmediateClickTracker,
  };
};

export type Analytics = ReturnType<typeof createAnalytics>;

const { getInstance, setInstance, resetInstance } = makeSingleton(createAnalytics);

export const useAnalytics = getInstance;
export const setAnalytics = setInstance;
export const resetAnalytics = resetInstance;
